import React, { useEffect, useMemo, useRef, useState } from "react";
import { PenTool, Save, Download } from "lucide-react";
import { CaptureUpdateAction, Excalidraw } from "@excalidraw/excalidraw";
import { Button } from "@/components/ui/button";
import type { Channel } from "@/utils/custom-types";
import "@excalidraw/excalidraw/index.css";
import type { ExcalidrawElement, FileId } from "@excalidraw/excalidraw/element/types";
import { useTheme } from "next-themes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, trpc } from "@/utils/trpc";
import { BoardTitle } from "../ui/board-title";
import { useSubscription } from "@trpc/tanstack-react-query";
import type { AppState, BinaryFileData, BinaryFiles, DataURL, NormalizedZoomValue, Zoom } from "@excalidraw/excalidraw/types";
import { ServerWsEvent, type ServerEventMap } from "server/websocket-events";
import { toast } from "sonner";

type CollaborationState = {
	x: number;
	y: number;
	tool: "pointer" | "laser";
	button: "down" | "up";
	selectedElementIds?: AppState["selectedElementIds"];
	scrollX: number;
	scrollY: number;
	zoom: Zoom;
	viewportWidth: number;
  	viewportHeight: number;
	followingUser: string;
};

type Pointer = {
	id: string;
	cur: CollaborationState;     
	target: CollaborationState;  
	lastUpdate: number;                
};

export interface BoardViewProps {
	workspaceId: string;
	channelId?: string;
	channel?: Channel | null;
	session?: any;
	currentWorkspace?: any;
}

export const BoardView: React.FC<BoardViewProps> = ({
	workspaceId,
	channelId,
	channel,
	session,
	currentWorkspace,
}) => {
	const { theme, setTheme } = useTheme();
	const [excalidrawAPI, setExcalidrawAPI] = useState<any>(null);

	const boardStateQueryKey = trpc.board.getBoardState.queryKey({
		channelId: Number(channelId)
	});

	const validChannel = channel?.type === "board";

	const { data: boardStateData, isFetched, isLoading: boardStateLoading } = useQuery({
		...trpc.board.getBoardState.queryOptions({
			channelId: Number(channelId),
		}),
		enabled: validChannel,
		refetchOnMount: 'always',       
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	const { data: boardImagesData } = useQuery({
		...trpc.board.getBoardImages.queryOptions({
			channelId: Number(channelId),
		}),
		enabled: validChannel,
		refetchOnMount: "always",
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});

	const updateBoardPresenceWebSocket = useMutation(
		trpc.board.updateBoardPresenceWebSocket.mutationOptions(),
	);

	const updateDisconnectStateWebSocket = useMutation(
		trpc.board.updateDisconnectStateWebSocket.mutationOptions(),
	);

	const updateElementLiveStateWebSocket = useMutation(
		trpc.board.updateElementLiveStateWebSocket.mutationOptions({
		}),
	);

	const createPresignedImageUploadMutation = useMutation(
		trpc.board.createPresignedImageUpload.mutationOptions(),
	);

	const saveBoardImagesMutation = useMutation(
		trpc.board.saveBoardImages.mutationOptions(),
	);

	const prevCollaborationStateRef = useRef<CollaborationState | null>(null);
	const collaborationStateRef = useRef<CollaborationState | null>(null);

	const prevElementsLiveStateRef = useRef<ExcalidrawElement[] | null>(null);
	const elementsStateRef = useRef<ExcalidrawElement[] | null>(null);
	
	const followingUser = useRef<string | null>(null);
	
	const pointersRef = useRef<Map<string, Pointer>>(new Map());
	const rafIdRef = useRef<number | null>(null);

	const ignoreChangesRef = useRef<Map<string, ExcalidrawElement[]>>(new Map());
	const knownFileIdsRef = useRef<Set<string>>(new Set());
	const loadedFileIdsRef = useRef<Set<string>>(new Set());
	const isLoadingFilesRef = useRef(false);
	const fileLoadRetryRef = useRef<{ timerId: number | null; attempt: number; lastKey: string }>({
		timerId: null,
		attempt: 0,
		lastKey: "",
	});
	const inactivityTimerRef = useRef<number | null>(null);
	const isDisconnectedRef = useRef(false);

	const DEBOUNCE_MS = 30;
	const DEBOUNCE_MS_ELEMENTS_LIVE = 30;
	const FILE_LOAD_RETRY_MS = 750;
	const FILE_LOAD_RETRY_MAX = 4;

	function mergeElements(
		currentElementState: ExcalidrawElement[],
		remoteElementUpdate: ExcalidrawElement[],
	): ExcalidrawElement[] {
		const byId = new Map<string, ExcalidrawElement>();
		for (const el of currentElementState) byId.set(el.id, el);
	  
		for (const incoming of remoteElementUpdate) {
			const existing = byId.get(incoming.id);
		
			if (!existing) {
				byId.set(incoming.id, incoming);
				continue;
			}
		
			if (incoming.version > existing.version) {
				byId.set(incoming.id, incoming);
			} else if (incoming.version === existing.version) {
				if (incoming.versionNonce > existing.versionNonce) {
					byId.set(incoming.id, incoming);
				}
			}
		}
	  
		return [...byId.values()];
	}

	type UploadFileMeta = {
		fileId: string;
		contentType: string;
		blob: Blob;
		size: number;
	};

	type BoardImageMeta = {
		key: string;
		bucket: string;
		contentType?: string | null;
		size?: number | null;
	};

	const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
		const res = await fetch(dataURL);
		return await res.blob();
	};

	const blobToDataURL = (blob: Blob): Promise<DataURL> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as DataURL);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(blob);
		});

	const uploadPendingFiles = async (files: BinaryFiles) => {
		if (!channelId) return [];
		if (!files || Object.keys(files).length === 0) return [];

		const entries = Object.entries(files);
		const metas: UploadFileMeta[] = [];

		for (const [fileId, f] of entries) {
			const contentType = f.mimeType || "application/octet-stream";
			const blob = await dataURLToBlob(f.dataURL);
			
			metas.push({
				fileId,
				contentType,
				blob,
				size: blob.size,
			})
		}

		const presignRes = await createPresignedImageUploadMutation.mutateAsync({
			channelId: Number(channelId),
			files: metas.map(a => ({
				fileId: a.fileId,
				contentType: a.contentType || "application/octet-stream",
			}))
		});

		const metaById = new Map(metas.map((meta) => [meta.fileId, meta]));

		await Promise.all(
			presignRes.uploads.map(async (u) => {
				const file = metaById.get(u.fileId);
				if (!file) {
					throw new Error(`Missing file payload for ${u.fileId}`);
				}
				const putRes = await fetch(u.uploadUrl, {
					method: "PUT",
					headers: { "Content-Type": file.contentType || "application/octet-stream" },
					body: file.blob,
				});

				if (!putRes.ok) {
					toast.error(`Upload failed (${putRes.status})`);
					throw new Error(`Upload failed (${putRes.status})`);
				}
			})
		)

		await saveBoardImagesMutation.mutateAsync({
			channelId: Number(channelId),
			files: presignRes.uploads.map((u) => ({
				fileId: u.fileId,
				key: u.key,
				contentType: u.contentType,
				size: metaById.get(u.fileId)?.size,
			})),
		});

		return presignRes.uploads.map(u => ({
			url: u.src,
			type: u.contentType,
			// name: u.originalName,
			// size: u.size,
		}));
	}

	const getImageElementsForFiles = (
		elements: readonly ExcalidrawElement[],
		fileIds: readonly string[],
	): ExcalidrawElement[] => {
		if (elements.length === 0 || fileIds.length === 0) return [];
		const fileIdSet = new Set(fileIds);
		return elements.filter((el) => {
			if (el.type !== "image") return false;
			const fileId = (el as { fileId?: string }).fileId;
			return typeof fileId === "string" && fileIdSet.has(fileId);
		});
	};

	const logBoardFilesAdded = async (
		addedFiles: BinaryFiles,
		addedElements: readonly ExcalidrawElement[],
	) => {
		try {
			// console.log("[board] files added", addedFiles);
			// console.log("[board] image elements added", addedElements);
			await uploadPendingFiles(addedFiles);
		} catch (error) {
			// console.log("[board] failed to log added files", error);
		}
	};

	const getImageFileIds = (elements: readonly ExcalidrawElement[]) => {
		const ids = new Set<string>();
		for (const el of elements) {
			if (el.type !== "image") continue;
			const fileId = (el as { fileId?: string }).fileId;
			if (typeof fileId === "string") ids.add(fileId);
		}
		return [...ids];
	};

	const getMissingImageFileIds = (elements: readonly ExcalidrawElement[]) => {
		if (!excalidrawAPI) return [];
		const existingFiles = excalidrawAPI.getFiles?.() ?? {};
		return getImageFileIds(elements).filter((fileId) => {
			if (loadedFileIdsRef.current.has(fileId)) return false;
			return !existingFiles[fileId];
		});
	};

	const resetFileLoadRetry = () => {
		if (fileLoadRetryRef.current.timerId != null) {
			clearTimeout(fileLoadRetryRef.current.timerId);
		}
		fileLoadRetryRef.current = { timerId: null, attempt: 0, lastKey: "" };
	};

	const scheduleFileLoadRetry = (
		missingFileIds: string[],
		elements: readonly ExcalidrawElement[],
	) => {
		if (missingFileIds.length === 0) {
			resetFileLoadRetry();
			return;
		}

		const key = missingFileIds.slice().sort().join("|");
		const state = fileLoadRetryRef.current;
		if (state.lastKey !== key) {
			state.lastKey = key;
			state.attempt = 0;
		}

		if (state.timerId != null) return;
		if (state.attempt >= FILE_LOAD_RETRY_MAX) return;

		const delay = FILE_LOAD_RETRY_MS * (state.attempt + 1);
		state.attempt += 1;
		state.timerId = window.setTimeout(() => {
			state.timerId = null;
			void ensureBoardFilesForElements(elements);
		}, delay);
	};

	const loadBoardImageFiles = async (
		images: Record<string, BoardImageMeta>,
		elements: readonly ExcalidrawElement[],
	) => {
		if (!channelId) return;
		if (!excalidrawAPI) return;
		if (Object.keys(images).length === 0) return;

		const imageFileIds = new Set(
			elements
				.filter((el) => el.type === "image")
				.map((el) => (el as { fileId?: string }).fileId)
				.filter((id): id is string => typeof id === "string"),
		);

		const existingFiles = excalidrawAPI.getFiles?.() ?? {};
		const missingFileIds = [...imageFileIds].filter((fileId) => {
			if (!images[fileId]) return false;
			if (loadedFileIdsRef.current.has(fileId)) return false;
			return !existingFiles[fileId];
		});

		if (missingFileIds.length === 0) return;

		isLoadingFilesRef.current = true;
		try {
			const files = await Promise.all(
				missingFileIds.map(async (fileId) => {
					const meta = images[fileId];
					if (!meta) return null;

					const { url } = await queryClient.fetchQuery({
						...trpc.board.getPresignedImageDownloadUrl.queryOptions({
							channelId: Number(channelId),
							key: meta.key,
						}),
						staleTime: 0,
					});

					const res = await fetch(url);
					if (!res.ok) {
						throw new Error(`Failed to download image (${res.status})`);
					}

					const blob = await res.blob();
					const dataURL = await blobToDataURL(blob);
					const mimeType = (meta.contentType || blob.type || "application/octet-stream") as BinaryFileData["mimeType"];

					return {
						id: fileId as FileId,
						dataURL,
						mimeType,
						created: Date.now(),
					} satisfies BinaryFileData;
				}),
			);

			const ready = files.filter((file): file is BinaryFileData => Boolean(file));
			if (ready.length > 0) {
				excalidrawAPI.addFiles(ready);
				for (const file of ready) {
					loadedFileIdsRef.current.add(file.id);
				}
			}
		} catch (error) {
			// console.log("[board] failed to load image files", error);
		} finally {
			isLoadingFilesRef.current = false;
		}
	};

	const ensureBoardFilesForElements = async (
		elements: readonly ExcalidrawElement[],
	) => {
		if (!channelId) return;
		if (!excalidrawAPI) return;

		const missingFileIds = getMissingImageFileIds(elements);
		if (missingFileIds.length === 0) {
			resetFileLoadRetry();
			return;
		}

		if (isLoadingFilesRef.current) {
			scheduleFileLoadRetry(missingFileIds, elements);
			return;
		}

		const images = await queryClient.fetchQuery({
			...trpc.board.getBoardImages.queryOptions({
				channelId: Number(channelId),
			}),
			staleTime: 0,
		});

		await loadBoardImageFiles(
			images as Record<string, BoardImageMeta>,
			elements,
		);

		const stillMissing = getMissingImageFileIds(elements);
		scheduleFileLoadRetry(stillMissing, elements);
	};

	function onRemotePointer(id: string, collabState: CollaborationState) {
		const map = pointersRef.current;
		const p = map.get(id);
		const now = performance.now();

		if (p) {
		  	p.target = collabState
			p.lastUpdate = now;		
		} else {
			map.set(id, {
				id,
				cur: { ...collabState },
				target: { ...collabState },
				lastUpdate: now,
			});
		}
	}

	function removeCollaborator(id: string) {
		const map = pointersRef.current;
		const p = map.get(id);

		if (p) {
		  	map.delete(id);	
		}

		const collaborators = new Map();
		for (const [id, pos] of map) {
			collaborators.set(id, {
				id,
				username: id,
				pointer: { x: pos.cur.x, y: pos.cur.y, tool: pos.cur.tool, renderCursor: true },
				button: pos.cur.button,
				selectedElementIds: pos?.cur.selectedElementIds ?? {}
			});
		}

		excalidrawAPI.updateScene({
			collaborators,
			captureUpdate: CaptureUpdateAction.NEVER
		});
	}

	const computeFollowerZoom = (
		leader: CollaborationState,
		followerAppState: AppState,
	): Zoom => {
		const W_L = leader.viewportWidth;
		const H_L = leader.viewportHeight;
		const W_F = followerAppState.width;
		const H_F = followerAppState.height;
	  
		if (!W_L || !H_L || !W_F || !H_F) {
		  return leader.zoom;
		}
	  
		const Z_L = leader.zoom.value;
	  
		const Z_F_byWidth = (W_F / W_L) * Z_L;
		const Z_F_byHeight = (H_F / H_L) * Z_L;
	  
		let value = Math.min(Z_F_byWidth, Z_F_byHeight);
	  
		const MIN_Z = 0.1;
		const MAX_Z = 4;
		if (value < MIN_Z) value = MIN_Z;
		if (value > MAX_Z) value = MAX_Z;
	  
		return { value: value as NormalizedZoomValue };
	};

	function startPointerSmoothing(updateScene: (positions: Map<string, CollaborationState>) => void) {
		const tick = () => {
		  const map = pointersRef.current;
		  let changed = false;
	  
		  const alpha = 0.25;
	  
		  for (const p of map.values()) {
			const dx = p.target.x - p.cur.x;
			const dy = p.target.y - p.cur.y;
			// snap if very close
			if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
			  p.cur = p.target;
			} else {
				p.cur.x += dx * alpha;
				p.cur.y += dy * alpha;
				p.cur.tool = p.target.tool;
				p.cur.button = p.target.button;
				p.cur.selectedElementIds = p.target.selectedElementIds;
			}

			if (p.id === followingUser.current) {
				if (p.target.followingUser === session.user.name) {
				} else {
					const appState = excalidrawAPI.getAppState();

					const followerZoom = computeFollowerZoom(p.target, appState);

					excalidrawAPI.updateScene({
						appState: {
							...appState,
							scrollX: p.target.scrollX,
							scrollY: p.target.scrollY,
							zoom: followerZoom,
						},
						captureUpdate: CaptureUpdateAction.NEVER,
					});
				}
			}	

			changed = true;
		  }

		  if (changed) {
			const positions = new Map<string, any>();
			for (const p of pointersRef.current.values()) {
				const collabObj = {
					x: p.cur.x, 
					y: p.cur.y, 
					tool: p.cur.tool, 
					button: p.cur.button, 
					selectedElementIds: p.cur.selectedElementIds,
				}

				positions.set(p.id, collabObj);
			}
			updateScene(positions);
		  }
	  
		  rafIdRef.current = requestAnimationFrame(tick);
		};
	  
		if (rafIdRef.current == null) rafIdRef.current = requestAnimationFrame(tick);
	}
	  
	function stopPointerSmoothing() {
		if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
		rafIdRef.current = null;
	}

	const collabEqual = (a: CollaborationState | null, b: CollaborationState | null) => {
		if (a === b) return true;                
		if (!a || !b) return false;  

		if (
			a.x !== b.x ||
			a.y !== b.y ||
			a.tool !== b.tool ||
			a.button !== b.button ||
			a.scrollX !== b.scrollX ||
			a.scrollY !== b.scrollY ||
			a.zoom?.value !== b.zoom?.value
		) {
			return false;
		}

		return true;
	};

	const elementsEqual = (a: ExcalidrawElement[] | null, b: ExcalidrawElement[] | null) => {
		if (a === b) {
			return true;
		}
		if (!a || !b) {
			return false;
		}

		if (a.length !== b.length) {
			return false;
		}
	
		for (let i = 0; i < a.length; i++) {
			const oldEl = a[i];
			const newEl = b[i];

			const angleChanged = Math.abs((oldEl.angle ?? 0) - (newEl.angle ?? 0)) > 0.0001;

			if (
				oldEl.id !== newEl.id ||
				oldEl.type !== newEl.type ||
				oldEl.x !== newEl.x ||
				oldEl.y !== newEl.y ||
				oldEl.width !== newEl.width ||
				oldEl.height !== newEl.height ||
				oldEl.strokeColor !== newEl.strokeColor ||
				oldEl.isDeleted !== newEl.isDeleted ||
				angleChanged
			) {
				return false;
			}
		}
	
		return true;
	};

	const createUpdate = (old: ExcalidrawElement[] | null, cur: ExcalidrawElement[] | null): ExcalidrawElement[] => {
		const oldMap = new Map<string, ExcalidrawElement>();
		for (const el of old || []) {
			oldMap.set(el.id, el);
		}

		const updatedElements: ExcalidrawElement[] = [];
		for (const el of cur || []) {
			const prev = oldMap.get(el.id);

			if (
				!prev ||
				prev.type !== el.type ||
				prev.x !== el.x ||
				prev.y !== el.y ||
				prev.width !== el.width ||
				prev.height !== el.height ||
				prev.strokeColor !== el.strokeColor ||
				prev.isDeleted !== el.isDeleted ||
				Math.abs((prev.angle ?? 0) - (el.angle ?? 0)) > 0.0001
			) {
				updatedElements.push(el);
			}
		}
		return updatedElements;
	};

	useSubscription(
		trpc.board.subscribeToBoardRedis.subscriptionOptions(
			{ boardId: Number(channelId) },
			{
				onData: (event) => {
					if (event?.type === ServerWsEvent.CollabStateUpdated) {
						const data = event.data as unknown as ServerEventMap[ServerWsEvent.CollabStateUpdated];
						// console.log("[received collabStateUpdated]", data);

						const eventCollabState = data.collabState;

						onRemotePointer(data.username, eventCollabState);
					} else if (event?.type === ServerWsEvent.CollabDisconnectUpdated) {
						const data = event.data as unknown as ServerEventMap[ServerWsEvent.CollabDisconnectUpdated];
						removeCollaborator(data.username);
					} else if (event?.type === ServerWsEvent.ElementStateUpdated) {
						const data = event.data as unknown as ServerEventMap[ServerWsEvent.ElementStateUpdated];
						// console.log("Received event elementStateUpdate: ", data);
						const elements = data.elementState;
						ignoreChangesRef.current.set(data.username, elements);

						const currentElementState = excalidrawAPI.getSceneElementsIncludingDeleted();
						const updatedState = mergeElements(currentElementState, elements);

						excalidrawAPI.updateScene({
							elements: updatedState,
							captureUpdate: CaptureUpdateAction.NEVER,
						});

						void ensureBoardFilesForElements(updatedState);
					}
				},
				onError: (err: unknown) => {
					console.error("[board event error]", err);
				},
			},
		),
	);

	useEffect(() => {
		if (!excalidrawAPI) return;
	  
		startPointerSmoothing((positions) => {
		  const collaborators = new Map();
		  for (const [id, pos] of positions) {
			collaborators.set(id, {
			  id,
			  username: id,
			  pointer: { x: pos.x, y: pos.y, tool: pos.tool, renderCursor: true },
			  button: pos.button,
			  selectedElementIds: pos?.selectedElementIds ?? {}
			});
		  }
		  excalidrawAPI.updateScene({
			collaborators,
			captureUpdate: CaptureUpdateAction.NEVER
		  });
		});
	  
		return () => stopPointerSmoothing();
	}, [excalidrawAPI]);

	useEffect(() => {
		const id = setInterval(() => {
			const prev = prevCollaborationStateRef.current;
  			const cur = collaborationStateRef.current;	

			if (!collabEqual(prev, cur)) {
				// console.log("[current collab] : ", {collaborationStateRef});
				prevCollaborationStateRef.current = collaborationStateRef.current;

				// console.log("update Board Presence Web socket with channelID", channelId);
				updateBoardPresenceWebSocket.mutate({
					channelId: Number(channelId),
					collabState: cur
				})
			}
		}, DEBOUNCE_MS);

		return () => clearInterval(id);
	}, [channelId])

	useEffect(() => {
		const id = setInterval(() => {
			const prev = prevElementsLiveStateRef.current;
  			const cur = elementsStateRef.current;	

			if (!elementsEqual(prev, cur)) {
				// console.log("Setting Query Cache");
				queryClient.setQueryData<ExcalidrawElement[] | null>(boardStateQueryKey, cur ? JSON.parse(JSON.stringify(cur)) : null);
				prevElementsLiveStateRef.current = cur ? JSON.parse(JSON.stringify(cur)) : null;

				const update = createUpdate(prev, cur);

				updateElementLiveStateWebSocket.mutate({
					channelId: Number(channelId),
					elementState: update
				})
			}
		}, DEBOUNCE_MS_ELEMENTS_LIVE);
		
		return () => {
			clearInterval(id);
		}
	}, [channelId, boardStateData])

	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = (e: MediaQueryListEvent) => {
			setTheme(e.matches ? "dark" : "light");
		};

		// Listen for theme changes
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	useEffect(() => {
		return () => {
			updateDisconnectStateWebSocket.mutate({
				channelId: Number(channelId),
			})

			resetFileLoadRetry();
			pointersRef.current.clear();
  			prevCollaborationStateRef.current = null;
		};
	}, [currentWorkspace.id, channelId]);

	// Track browser tab visibility for away detection
	useEffect(() => {
		if (!validChannel || !excalidrawAPI) return;

		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Tab is hidden - user is away
				if (!isDisconnectedRef.current) {
					isDisconnectedRef.current = true;
					updateDisconnectStateWebSocket.mutate({
						channelId: Number(channelId),
					});
				}
			} else {
				// Tab is visible again - user is back
				// Reset disconnect flag so presence updates can resume
				isDisconnectedRef.current = false;
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [channelId, validChannel, excalidrawAPI]);

	// Track user inactivity for away detection
	useEffect(() => {
		if (!validChannel || !excalidrawAPI) return;

		const INACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes

		const resetInactivityTimer = () => {
			// User is interacting - reset disconnect flag so presence updates can resume
			if (isDisconnectedRef.current) {
				isDisconnectedRef.current = false;
			}

			// Clear existing timer
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
				inactivityTimerRef.current = null;
			}

			// Don't set timer if tab is hidden (visibility API handles that)
			if (document.hidden) return;

			// Set new timer
			inactivityTimerRef.current = window.setTimeout(() => {
				// User has been inactive for 2 minutes
				if (!isDisconnectedRef.current && !document.hidden) {
					isDisconnectedRef.current = true;
					updateDisconnectStateWebSocket.mutate({
						channelId: Number(channelId),
					});
				}
			}, INACTIVITY_TIMEOUT);
		};

		// Track various user interactions
		const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
		events.forEach(event => {
			document.addEventListener(event, resetInactivityTimer, { passive: true });
		});

		// Initialize timer
		resetInactivityTimer();

		// Reset timer when tab becomes visible
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				isDisconnectedRef.current = false;
				resetInactivityTimer();
			} else {
				// Clear timer when tab is hidden
				if (inactivityTimerRef.current) {
					clearTimeout(inactivityTimerRef.current);
					inactivityTimerRef.current = null;
				}
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			events.forEach(event => {
				document.removeEventListener(event, resetInactivityTimer);
			});
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
				inactivityTimerRef.current = null;
			}
		};
	}, [channelId, validChannel, excalidrawAPI]);

	useEffect(() => {
		if (!excalidrawAPI) return;
		if (!boardStateData) return;
		
		// Get current scene elements
		const currentElements = excalidrawAPI.getSceneElementsIncludingDeleted();
		
		// Only update if server data is different and we're not in the middle of local changes
		if (boardStateData.length === 0 && currentElements.length === 0) return;
		
		// Merge server state with current state to preserve local changes
		const mergedElements = mergeElements(currentElements, boardStateData);
		
		excalidrawAPI.updateScene({
			elements: mergedElements,
			captureUpdate: CaptureUpdateAction.NEVER,
		});
	}, [excalidrawAPI, boardStateData])

	useEffect(() => {
		if (!excalidrawAPI) return;
		if (!boardStateData) return;
		if (!boardImagesData) return;
		if (isLoadingFilesRef.current) return;

		loadBoardImageFiles(
			boardImagesData as Record<string, BoardImageMeta>,
			boardStateData,
		);

		const missingAfterLoad = getMissingImageFileIds(boardStateData);
		scheduleFileLoadRetry(missingAfterLoad, boardStateData);
	}, [boardImagesData, boardStateData, excalidrawAPI]);

	// Show spinner only when no cached data exists (initial load)
	// When cached data exists, show it immediately while refetch happens in background
	if (validChannel && boardStateLoading && !boardStateData) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Excalidraw Container with CSS Reset */}
			<div
				className="flex-1 relative excalidraw-wrapper"
				style={{
					height: "calc(100vh - 120px)",
					width: "100%",
					isolation: "isolate",
				}}
			>
				<Excalidraw
					key={channelId}
					theme={theme === "dark" ? "dark" : "light"}
					excalidrawAPI={(api) => setExcalidrawAPI(api)}
					onChange={(
						elements: readonly ExcalidrawElement[],
						appState: AppState,
						files: BinaryFiles
					) => {
						if (files && Object.keys(files).length > 0) {
							const currentIds = Object.keys(files);
							const knownIds = knownFileIdsRef.current;
							const addedIds = currentIds.filter((id) => !knownIds.has(id));

							if (addedIds.length > 0) {
								const addedFiles = Object.fromEntries(
									addedIds.map((id) => [id, files[id]]),
								) as BinaryFiles;
								const addedElements = getImageElementsForFiles(elements, addedIds);
								logBoardFilesAdded(addedFiles, addedElements);
							}

							knownFileIdsRef.current = new Set(currentIds);
						} else {
							knownFileIdsRef.current.clear();
						}

						for (const [username, pendingEls] of ignoreChangesRef.current.entries()) {
							if (elementsEqual([...elements], pendingEls)) {
								ignoreChangesRef.current.delete(username);
								const snapshot = JSON.parse(JSON.stringify(elements));
								prevElementsLiveStateRef.current = snapshot;
								elementsStateRef.current = snapshot;
								return;
							}
						}

						const userToFollow = appState.userToFollow ? appState.userToFollow.username : null;

						collaborationStateRef.current = {
							...(collaborationStateRef.current ?? {
								x: 0,
								y: 0,
								tool: "pointer",
								button: "up",
							}),
							selectedElementIds: appState.selectedElementIds,
							scrollX: appState.scrollX,
							scrollY: appState.scrollY,
							zoom: appState.zoom,
							viewportWidth: appState.width,
    						viewportHeight: appState.height,
							followingUser: userToFollow ?? ""
						};	

						const snapshot = JSON.parse(JSON.stringify(elements));
						elementsStateRef.current = snapshot;

						followingUser.current = userToFollow;
					}}
					onPointerUpdate={(
						payload
					) => {
						const cur = collaborationStateRef.current;

						const statePayload = {
							x: payload.pointer.x,
							y: payload.pointer.y,
							tool: payload.pointer.tool,
							button: payload.button,
							selectedElementIds: cur?.selectedElementIds ?? {},
							scrollX: cur?.scrollX ?? 0,
							scrollY: cur?.scrollY ?? 0,
							zoom: cur?.zoom ?? { value: 1 as NormalizedZoomValue },
							viewportWidth: cur?.viewportWidth ?? 0,
							viewportHeight: cur?.viewportHeight ?? 0,
							followingUser: cur?.followingUser ?? ""
						};

						collaborationStateRef.current = statePayload;
					}}
					initialData={{
						elements: boardStateData
					}}
					UIOptions={{
						canvasActions: {
							export: { saveAsImage: true },
							loadScene: true,
							saveToActiveFile: false,
							theme: true,
						},
					}}
					langCode="en"
					viewModeEnabled={false}
					zenModeEnabled={false}
					gridModeEnabled={false}
				/>
			</div>
		</div>
	);
};
